/**
 * Health Status Widget Component
 * Displays RAG (Red/Amber/Green) health indicators for projects
 */
import React, { useState, useEffect } from 'react';
import { healthAPI } from '../services/api';
import '../styles/HealthStatusWidget.css';

const HealthStatusWidget = ({ projectId = null, showDetails = true }) => {
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchHealthData();
  }, [projectId]);

  const fetchHealthData = async () => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = projectId
        ? () => healthAPI.getProjectHealth(projectId)
        : () => healthAPI.getHealthSummary();

      const response = await endpoint();
      setHealthData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Sağlık durumu yüklenemedi');
      console.error('Health data fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchHealthData();
    setRefreshing(false);
  };

  const getStatusColor = (status) => {
    const colors = {
      green: '#10b981',
      amber: '#f59e0b',
      red: '#ef4444',
      grey: '#6b7280'
    };
    return colors[status?.toLowerCase()] || colors.grey;
  };

  const getStatusIcon = (status) => {
    const icons = {
      green: '✅',
      amber: '⚠️',
      red: '🔴',
      grey: '⚪'
    };
    return icons[status?.toLowerCase()] || '⚪';
  };

  const getStatusText = (status) => {
    const texts = {
      green: 'Sağlıklı',
      amber: 'Dikkat',
      red: 'Risk',
      grey: 'Bilinmiyor'
    };
    return texts[status?.toLowerCase()] || 'Bilinmiyor';
  };

  if (loading) {
    return (
      <div className="health-widget loading">
        <div className="spinner"></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="health-widget error">
        <div className="error-icon">⚠️</div>
        <p>{error}</p>
        <button onClick={fetchHealthData}>Tekrar Dene</button>
      </div>
    );
  }

  // Project Detail View
  if (projectId && healthData) {
    return (
      <div className="health-widget project-detail">
        <div className="widget-header">
          <h3>
            <span className="icon">💚</span>
            {healthData.project_name || 'Proje Sağlığı'}
          </h3>
          <button
            className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            🔄
          </button>
        </div>

        {/* Overall Status */}
        <div
          className="overall-status"
          style={{ backgroundColor: getStatusColor(healthData.overall_status) }}
        >
          <div className="status-content">
            <span className="score">{Math.round(healthData.overall_score)}</span>
            <span className="status-label">
              {getStatusIcon(healthData.overall_status)} {getStatusText(healthData.overall_status)}
            </span>
          </div>
        </div>

        {/* Category Indicators */}
        {showDetails && (
          <div className="indicators-grid">
            <div className="indicator">
              <div className="indicator-header">
                <span className="indicator-icon">📅</span>
                <span className="indicator-title">Takvim</span>
              </div>
              <div
                className="indicator-status"
                style={{ backgroundColor: getStatusColor(healthData.schedule_status) }}
              >
                {getStatusText(healthData.schedule_status)}
              </div>
            </div>

            <div className="indicator">
              <div className="indicator-header">
                <span className="indicator-icon">💰</span>
                <span className="indicator-title">Bütçe</span>
              </div>
              <div
                className="indicator-status"
                style={{ backgroundColor: getStatusColor(healthData.budget_status) }}
              >
                {getStatusText(healthData.budget_status)}
              </div>
            </div>

            <div className="indicator">
              <div className="indicator-header">
                <span className="indicator-icon">📊</span>
                <span className="indicator-title">Kapsam</span>
              </div>
              <div
                className="indicator-status"
                style={{ backgroundColor: getStatusColor(healthData.scope_status) }}
              >
                {getStatusText(healthData.scope_status)}
              </div>
            </div>

            <div className="indicator">
              <div className="indicator-header">
                <span className="indicator-icon">👥</span>
                <span className="indicator-title">Kaynaklar</span>
              </div>
              <div
                className="indicator-status"
                style={{ backgroundColor: getStatusColor(healthData.resource_status) }}
              >
                {getStatusText(healthData.resource_status)}
              </div>
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="metrics-section">
          <div className="metric">
            <span className="metric-icon">⚠️</span>
            <div className="metric-content">
              <span className="metric-value">{healthData.overdue_tasks}</span>
              <span className="metric-label">Geciken</span>
            </div>
          </div>

          <div className="metric">
            <span className="metric-icon">🚫</span>
            <div className="metric-content">
              <span className="metric-value">{healthData.blocked_tasks}</span>
              <span className="metric-label">Engellenen</span>
            </div>
          </div>

          <div className="metric">
            <span className="metric-icon">✅</span>
            <div className="metric-content">
              <span className="metric-value">{Math.round(healthData.completion_percentage)}%</span>
              <span className="metric-label">Tamamlanma</span>
            </div>
          </div>
        </div>

        {healthData.notes && (
          <div className="health-notes">
            <strong>Not:</strong> {healthData.notes}
          </div>
        )}
      </div>
    );
  }

  // Summary View
  return (
    <div className="health-widget summary-view">
      <div className="widget-header">
        <h3>
          <span className="icon">📊</span>
          Proje Sağlık Durumu
        </h3>
        <button
          className={`refresh-btn ${refreshing ? 'spinning' : ''}`}
          onClick={handleRefresh}
          disabled={refreshing}
        >
          🔄
        </button>
      </div>

      <div className="summary-stats">
        <div className="stat-card total">
          <span className="stat-count">{healthData.total_projects}</span>
          <span className="stat-label">Toplam Proje</span>
        </div>

        <div className="stat-card green">
          <span className="stat-count">{healthData.green_projects}</span>
          <span className="stat-label">✅ Yeşil</span>
        </div>

        <div className="stat-card amber">
          <span className="stat-count">{healthData.amber_projects}</span>
          <span className="stat-label">⚠️ Sarı</span>
        </div>

        <div className="stat-card red">
          <span className="stat-count">{healthData.red_projects}</span>
          <span className="stat-label">🔴 Kırmızı</span>
        </div>
      </div>

      <div className="average-score-section">
        <div className="score-circle">
          <span className="score-value">{Math.round(healthData.average_score)}</span>
        </div>
        <span className="score-label">Ortalama Skor</span>
      </div>

      {healthData.at_risk_projects?.length > 0 && (
        <div className="at-risk-section">
          <h4>⚠️ Risk Altındaki Projeler</h4>
          <ul className="risk-list">
            {healthData.at_risk_projects.map(project => (
              <li key={project.id} className="risk-item">
                <span className="project-name">{project.name}</span>
                <span className="project-score" style={{ color: getStatusColor(project.status) }}>
                  {Math.round(project.score)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HealthStatusWidget;
